import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { FaChevronLeft } from "react-icons/fa";
import { apiFetch, getUser, hasAction } from "./utils/auth";
import { pickField } from "./utils/fieldHelpers";
import { humanError, errorText } from "./utils/apiError";
import SearchableSelect from "./components/SearchableSelect";
import AddressAutocompleteField from "./components/AddressAutocompleteField";
import CustomerContractTab from "./components/CustomerContractTab";
import { buildSalesRepConfig, AUTOCOMPLETE_TERRITORY } from "./utils/refDataConfigs";
import { fetchRoleCodes, ROLE_FALLBACK } from "./utils/roleCodes";
import { buildQuoteSearchQs } from "./Quote";
import { buildOrderSearchQs } from "./Order";
import { buildShipmentSearchQs } from "./Shipment";
import { csvCell } from "./utils/csvCell";

const API_URL = process.env.REACT_APP_API_URL;

// Google address autocomplete (same endpoints the create wizard uses). Picking a
// suggestion fans the details out to City / State / ZIP.
const ADDRESS_AUTOCOMPLETE_CONFIG = {
  searchUrl: "/api/AddressAutocomplete/search",
  detailsUrl: "/api/AddressAutocomplete/details",
  country: "US",
  minChars: 3,
};

// ── small shared helpers (kept local so this module is self-contained) ─────────
const f = (o, k, d = "") => { const v = pickField(o, k); return v === undefined || v === null ? d : v; };
const deriveTerritory = (rep) => { const i = (rep || "").indexOf("-"); return i > 0 ? rep.slice(0, i) : (rep || ""); };
const splitName = (full) => { const p = (full || "").trim().split(/\s+/); return { firstName: p[0] || "", lastName: p.slice(1).join(" ") || "" }; };
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const TERMINAL = ["completed", "failed", "rolledback", "compensated"];
const me = () => { try { const u = getUser() || {}; return u.email || u.userName || u.name || null; } catch { return null; } };
// Customer-level contacts have ShipToNum "", but a URL segment can't be empty,
// so the API takes "-" to mean customer level. It must not be "0" — that's a
// real ship-to number for some customers, and using it here sent edits and
// deletes of Sold-To contacts to an address that usually doesn't exist (404),
// or to the wrong one where it did.
const shipToSeg = (v) => { const s = String(v ?? "").trim(); return s === "" ? "-" : s; };
// Mirrors the server's PrimaryBillingRules.IsValidEmail: the billing recipient
// receives invoices and order communication, so a real address is required —
// not merely a non-blank one.
const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || "").trim());
const fmtDate = (s) => { if (!s) return "—"; const d = new Date(s); return isNaN(d) ? String(s) : d.toLocaleString(); };

// A ship-to is born with an auto-created contact. When the rep didn't name the
// receiver, the backend seeds a MARKED PLACEHOLDER whose title contains
// "placeholder". This mirrors the backend's PlaceholderContactPolicy, where the
// TITLE MARKER ALONE is authoritative — a previous extra "ShpRec and not
// primary-shipping" clause here false-badged every manually added ship-to
// contact left at the form defaults (role ShpRec, primary unticked), even
// though the backend treats those as real receivers (acks/shipping allowed).
const isPlaceholderContact = (ct) =>
  (f(ct, "contactTitle") || "").toLowerCase().includes("placeholder");

// Contacts carry the ship-to they belong to. Customer-level (Sold-To) contacts
// have a blank shipToNum; a value ties the contact to that delivery address.
const contactShipTo = (ct) => { const v = f(ct, "shipToNum"); return v === null || v === undefined ? "" : String(v); };

// DEF-14: a ship-to save or normalize succeeds even when Epicor refuses to move the
// primary; the server appends a "However, …" sentence to its message. Surface it.
const primaryNotSetText = (res) => {
  const m = String(f(res, "message", ""));
  const i = m.indexOf("However,");
  return i >= 0 ? m.slice(i) : "Saved, but the primary ship-to was not changed. Set the primary again.";
};

// Module-level field components (stable type → inputs never remount/lose focus).
function TextField({ label, value, onChange, span, mono, type = "text" }) {
  return (
    <div style={span ? { gridColumn: "1 / -1" } : undefined}>
      <label className="qw-label">{label}</label>
      <input className={"qw-input" + (mono ? " qw-mono" : "")} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
function CheckField({ label, checked, onChange }) {
  return (
    <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 13 }}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}

// Read-only counterpart to TextField — one labelled value in the Overview read-out.
// Desktop stacks label over value; on phones .qw-readrow lays them out as one
// tabulated row (label column left, value fills the right) — see QuoteWizard.css.
function ReadRow({ label, children, span }) {
  return (
    <div className="qw-readrow" style={span ? { gridColumn: "1 / -1" } : undefined}>
      <div className="qw-label">{label}</div>
      <div className="qw-readval">{children}</div>
    </div>
  );
}

// Fields tracked for the Overview diff. `num`/`bool` drive normalization + display.
const OV_FIELDS = [
  { k: "name", label: "Name" },
  { k: "customerType", label: "Customer Type" },
  { k: "legalName", label: "Legal Name" },
  { k: "address1", label: "Address 1" },
  { k: "city", label: "City" },
  { k: "state", label: "State" },
  { k: "zip", label: "ZIP" },
  { k: "phoneNum", label: "Phone" },
  { k: "eMailAddress", label: "Email" },
  { k: "salesRepCode", label: "Sales Rep" },
  { k: "termsCode", label: "Terms" },
  { k: "creditLimit", label: "Credit Limit", num: true },
  { k: "creditHold", label: "Credit Hold", bool: true },
  { k: "inactive", label: "Inactive", bool: true },
  { k: "hospitalType_c", label: "Hospital Type" },
  { k: "noOfBeds_c", label: "No. of Beds", num: true },
  { k: "traumaLevel_c", label: "Trauma Level", num: true },
  { k: "helipad_c", label: "Helipad", bool: true },
  { k: "hasContract_c", label: "Has Contract", bool: true },
];

// Split for the Overview read-out: the Shukla extensions stay tucked behind a
// disclosure the way they do in the edit form.
const OV_CORE = OV_FIELDS.filter((fd) => !fd.k.endsWith("_c"));
const OV_CUSTOM = OV_FIELDS.filter((fd) => fd.k.endsWith("_c"));

const norm = (v, fd) => {
  if (fd.bool) return !!v;
  if (fd.num) return v === "" || v === null || v === undefined ? null : Number(v);
  return v === null || v === undefined ? "" : String(v);
};
const disp = (v, fd) => {
  if (fd.bool) return v ? "Yes" : "No";
  if (fd.num) return v === null || v === undefined ? "—" : String(v);
  return v === "" || v === null || v === undefined ? "—" : String(v);
};

// Build the editable form's initial state from the loaded customer.
function editsFromCustomer(c) {
  const e = {};
  OV_FIELDS.forEach((fd) => {
    const raw = pickField(c, fd.k);
    if (fd.bool) e[fd.k] = !!raw;
    else e[fd.k] = raw === null || raw === undefined ? "" : String(raw);
  });
  return e;
}

// Diff edited vs original → { rows:[{ k,label,oldDisp,newDisp }], patch:{...changed} }.
function computeDiff(original, edits) {
  const rows = [];
  const patch = {};
  OV_FIELDS.forEach((fd) => {
    const o = norm(pickField(original, fd.k), fd);
    const n = norm(edits[fd.k], fd);
    if (o !== n) {
      rows.push({ k: fd.k, label: fd.label, oldDisp: disp(o, fd), newDisp: disp(n, fd) });
      patch[fd.k] = n;
    }
  });
  // Territory always follows the rep prefix (mirrors the create saga's TerritoryLock).
  if ("salesRepCode" in patch) patch.territoryID = deriveTerritory(patch.salesRepCode);
  return { rows, patch };
}

// PATCH /api/Customer/full/{custNum} → poll /api/Sales/job/{jobId} until terminal.
async function patchCustomerFull(custNum, patch) {
  const body = { ...patch, updatedBy: me() };
  const r = await apiFetch(`${API_URL}/api/Customer/full/${custNum}`, { method: "PATCH", body: JSON.stringify(body) });
  const res = await r.json().catch(() => ({}));
  if (!r.ok || res.success === false) throw new Error(humanError(r, res, "Couldn't save the customer changes."));
  const jobId = res.jobId || res.JobId;
  if (jobId) {
    for (let i = 0; i < 30; i++) {
      await wait(1000);
      const jr = await apiFetch(`${API_URL}/api/Sales/job/${jobId}`).catch(() => null);
      if (!jr || !jr.ok) continue;
      const job = await jr.json().catch(() => null);
      const status = String(job?.status || job?.Status || "").toLowerCase();
      if (TERMINAL.includes(status)) {
        if (status !== "completed") throw new Error(job?.failedReason || job?.FailedReason || `Job ${status}`);
        return job;
      }
    }
  }
  return null;
}

// =============================================================================
// DetailView — editable customer 360°: Overview (diff+review) / Contacts /
// Ship-Tos / Audit, plus credit-hold & inactive quick-toggles and delete.
// =============================================================================
export default function DetailView({ custNum, onBack, showToast, onChanged, onDeleted }) {
  const [tab, setTab] = useState("overview");
  const [customer, setCustomer] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [shiptos, setShiptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const [contactModal, setContactModal] = useState(null); // null | { contact, shipToNum }
  const [shipToModal, setShipToModal] = useState(null);
  const [reviewState, setReviewState] = useState(null);    // { patch, rows } when reviewing
  const [openShipTo, setOpenShipTo] = useState(null);      // shipToNum whose contacts panel is expanded
  const [ctReload, setCtReload] = useState(0);             // bump → re-run the open panel's scoped contacts fetch

  const toast = showToast || (() => {});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [cRes, ctRes, stRes] = await Promise.all([
        apiFetch(`${API_URL}/api/Customer/${custNum}`),
        // Deliberately UNSCOPED — every level in one call. The Contacts tab
        // shows only the customer-level slice (see customerContacts below),
        // but the Ship-Tos grid needs the ship-to ones for its per-card
        // "Manage contacts (N)" count and NEEDS RECEIVER badge. Fetching the
        // lot once and splitting locally beats two round-trips.
        apiFetch(`${API_URL}/api/CustCnt?custNum=${custNum}&count=true`).catch(() => null),
        apiFetch(`${API_URL}/api/ShipTo?custNum=${custNum}`).catch(() => null),
      ]);
      const cData = await cRes.json().catch(() => ({}));
      if (!cRes.ok) throw new Error(humanError(cRes, cData, "This customer couldn't be found — it may have been removed."));
      setCustomer(cData.customer || cData.Customer || cData);
      if (ctRes) { const d = await ctRes.json().catch(() => ({})); setContacts(d.contacts || d.Contacts || []); }
      if (stRes) { const d = await stRes.json().catch(() => ({})); setShiptos(d.shipToes || d.ShipToes || d.shipTos || []); }
    } catch (e) { setError(errorText(e, "Couldn't load this customer.")); }
    finally { setLoading(false); }
  }, [custNum]);

  useEffect(() => { load(); }, [load]);

  // Run a mutation with a busy guard; reload + notify the list on success.
  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast(okMsg);
      await load();
      onChanged && onChanged();
      return true;
    } catch (e) {
      toast(errorText(e, "That action couldn't be completed."), "err");
      return false;
    } finally { setBusy(false); }
  };

  // Toggle a boolean flag (creditHold / inactive) and VERIFY it actually stuck in
  // Epicor. The app's PATCH returns 200 even when Epicor silently ignores the change
  // — e.g. clearing a credit hold requires Credit Manager rights the integration user
  // may not have — so a non-throwing call is NOT proof it persisted. We re-read the
  // customer and compare the flag, then toast the TRUE outcome (no false "success").
  const quickToggle = async (field) => {
    const cur = !!f(customer, field, false);
    const target = !cur;
    setBusy(true);
    try {
      await patchCustomerFull(custNum, { [field]: target });

      // Re-read from Epicor (via the app) to see what actually landed.
      const vr = await apiFetch(`${API_URL}/api/Customer/${custNum}`);
      const vd = await vr.json().catch(() => ({}));
      if (!vr.ok) throw new Error(humanError(vr, vd, "Couldn't confirm the change."));
      const fresh = vd.customer || vd.Customer || vd;
      const now = !!f(fresh, field, false);
      setCustomer(fresh);
      onChanged && onChanged();

      if (now === target) {
        toast(field === "creditHold"
          ? (target ? "Placed on credit hold" : "Released credit hold")
          : (target ? "Marked inactive" : "Reactivated"));
        return true;
      }

      // Epicor did NOT accept the change — say so plainly rather than claim success.
      toast(field === "creditHold"
        ? `Epicor kept the account ${now ? "on credit hold" : "off hold"} — the change didn't stick. Changing credit hold requires Credit Manager rights in Epicor.`
        : `Epicor didn't apply the change — the account is still ${now ? "inactive" : "active"}.`,
        "err");
      return false;
    } catch (e) {
      toast(errorText(e, "That action couldn't be completed."), "err");
      return false;
    } finally { setBusy(false); }
  };

  const onDelete = () => {
    if (!window.confirm(`Delete customer ${f(customer, "custID")} (#${custNum})? This cannot be undone.`)) return;
    setBusy(true);
    apiFetch(`${API_URL}/api/Customer/${custNum}`, { method: "DELETE" })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't delete this customer."));
        toast(`Deleted ${f(customer, "custID")}`);
        onDeleted && onDeleted();
      })
      .catch((e) => toast(errorText(e, "Couldn't delete this customer."), "err"))
      .finally(() => setBusy(false));
  };

  // Make one contact THE primary billing contact (invoice/order recipient).
  // The dedicated endpoint enforces the exactly-one invariant server-side;
  // here we only ask before displacing the current holder.
  const makeBillingRecipient = (ct) => {
    const current = contacts.find(
      (c) => f(c, "primaryBilling", false)
        && (String(f(c, "conNum")) !== String(f(ct, "conNum")) || contactShipTo(c) !== contactShipTo(ct)));
    if (current && !window.confirm(
      `${f(current, "name")} is currently the primary billing contact. ` +
      `Make ${f(ct, "name")} the primary billing contact instead? Invoices and order emails will go to ${f(ct, "eMailAddress")}.`)) return;
    run(async () => {
      const r = await apiFetch(
        `${API_URL}/api/CustCnt/${custNum}/${encodeURIComponent(shipToSeg(f(ct, "shipToNum")))}/${f(ct, "conNum")}/primary-billing?value=true`,
        { method: "PATCH" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't set the primary billing contact."));
    }, `${f(ct, "name")} is now the primary billing contact`);
  };

  const deleteContact = (ct) => {
    if (!window.confirm(`Delete contact "${f(ct, "name")}"?`)) return;
    const shipToNum = shipToSeg(f(ct, "shipToNum"));
    const conNum = f(ct, "conNum");
    const q = me() ? `?deletedBy=${encodeURIComponent(me())}` : "";
    run(async () => {
      const r = await apiFetch(`${API_URL}/api/CustCnt/full/${custNum}/${encodeURIComponent(shipToNum)}/${conNum}${q}`, { method: "DELETE" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't delete this contact."));
    }, "Contact deleted");
  };

  // Count of contacts at a ship-to — drives the per-card "(N)" badge. Derived
  // from the bulk list for the badge only; the panel itself re-fetches scoped.
  const contactsForShipTo = (num) => contacts.filter((c) => contactShipTo(c) === String(num));

  // ── Splitting the two levels apart ────────────────────────────────────────
  // Epicor keeps the customer's own record under a BLANK key at both levels:
  // a contact with shipToNum "" is a Sold-To contact, and the ShipTo row with
  // shipToNum "" is the customer's own address rather than a delivery address.
  // Listing the blanks alongside the numbered rows made one person look like
  // two and showed the same address twice with nothing saying which was which.
  //
  // So: Contacts tab = customer level only, Ship-Tos tab = real (numbered)
  // delivery addresses only. The blank ship-to needs no row of its own — it
  // mirrors the customer record, which Overview already displays in full.
  const customerContacts = contacts.filter((c) => contactShipTo(c) === "");
  const numberedShipTos = shiptos.filter((s) => String(f(s, "shipToNum")).trim() !== "");

  const deleteShipTo = (st) => {
    if (f(st, "primaryShipTo", false)) { toast("Cannot delete the primary Ship-To — promote another first.", "err"); return; }
    // DEF-15: a customer always keeps at least one ship-to.
    if (numberedShipTos.length <= 1) { toast("Cannot delete the only Ship-To — add another first.", "err"); return; }
    if (!window.confirm(`Delete Ship-To #${f(st, "shipToNum")} (${f(st, "name")})?`)) return;
    const shipToNum = f(st, "shipToNum");
    run(async () => {
      const r = await apiFetch(`${API_URL}/api/ShipTo/${custNum}/${encodeURIComponent(shipToNum)}`, { method: "DELETE" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't delete this ship-to."));
    }, "Ship-To deleted");
  };

  const normalize = () => {
    if (!window.confirm("Normalize Ship-Tos? This re-syncs rep, territory and ship-via on every ship-to from the customer, and repairs the primary if it points at no ship-to. Ship-To numbers are not changed.")) return;
    run(async () => {
      const r = await apiFetch(`${API_URL}/api/ShipTo/${custNum}/normalize`, { method: "PATCH" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't normalize the ship-tos."));
      if (/However,/.test(f(d, "message", ""))) toast(primaryNotSetText(d), "err");
    }, "Ship-Tos normalized");
  };

  if (loading) return <div className="qw-card qw-pad" style={{ textAlign: "center", color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading customer…</div>;
  if (error) return (
    <div className="qw-card qw-pad">
      <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={onBack}><FaChevronLeft /> Back</button>
      <div className="qw-info qw-info-amber" style={{ marginTop: 10, background: "#FEF2F2", color: "#991B1B" }}>{error}</div>
    </div>
  );
  if (!customer) return null;

  const inactive = !!f(customer, "inactive", false), creditHold = !!f(customer, "creditHold", false);
  const rep = f(customer, "salesRepCode");
  // The ship-to whose contacts panel is expanded (re-resolved from the live list
  // so it survives reloads; undefined once its ship-to is deleted → panel hides).
  const openShipToObj = openShipTo === null ? null : shiptos.find((s) => String(f(s, "shipToNum")) === String(openShipTo));

  return (
    <div className="qw-fade">
      <div className="qw-card qw-pad" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={onBack}><FaChevronLeft /> All customers</button>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {/* Action entitlements (RoleActions): 00019 active/inactive, 00020 delete.
                Fallback true keeps buttons for sessions from before entitlements shipped
                (until re-login). No credit hold button: Epicor puts customers on and off
                hold itself, so it is the Epicor admin's job (SAL-GAP-65, Kapil 2026-10-22). */}
            {hasAction("00019", true) && <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={busy} onClick={() => quickToggle("inactive")}>{inactive ? "✓ Reactivate" : "💤 Inactive"}</button>}
            {hasAction("00020", true) && <button className="qw-btn qw-btn-danger qw-btn-sm" disabled={busy} onClick={onDelete}>🗑 Delete</button>}
          </div>
        </div>

        {/* Name, IDs and status details share one flex row; they wrap only when the
            viewport is too narrow to hold the whole line. */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 10, fontSize: 12, color: "#64748B" }}>
          <h2 className="qw-h2" style={{ margin: 0 }}>{f(customer, "name")}</h2>
          <span className="qw-pill qw-pill-indigo qw-mono">{f(customer, "custID")}</span>
          <span className="qw-pill qw-pill-slate qw-mono">#{f(customer, "custNum")}</span>
          <span className={"qw-pill " + (inactive ? "qw-pill-slate" : creditHold ? "qw-pill-amber" : "qw-pill-emerald")}>{inactive ? "INACTIVE" : creditHold ? "CREDIT HOLD" : "ACTIVE"}</span>
          <span className="qw-pill qw-pill-indigo">{f(customer, "customerType", "CUS")}</span>
          {/* T-012: the leading "·" separators dangled at the start of wrapped lines
              on phones (mis-aligned look). Hide them < 992px so items separate by the
              flex gap; desktop keeps the inline "·" exactly as before. */}
          <span><span className="d-none d-lg-inline">· </span>{f(customer, "termsCode", "N30")}</span>
          <span><span className="d-none d-lg-inline">· </span><span className="qw-mono">{rep || "—"}</span></span>
          <span><span className="d-none d-lg-inline">· </span>Territory <span className="qw-mono" style={{ color: "#4F46E5" }}>{f(customer, "territoryID") || deriveTerritory(rep) || "—"}</span></span>
        </div>

        {/* Contract (00082 = view price contracts) is gated with fallback FALSE,
            unlike every other hasAction call in this file. The `true` fallback
            elsewhere protects sessions that predate entitlements from losing
            buttons they used to have; a session that predates price contracts
            can never carry 00082, so a `true` fallback here would show a tab the
            API answers with 403. Hidden until the user signs in again. */}
        <div style={{ display: "flex", gap: 8, marginTop: 14, borderBottom: "1px solid #E2E8F0", flexWrap: "wrap" }}>
          {[["overview", "Overview"], ["contacts", `Contacts (${customerContacts.length})`], ["shiptos", `Ship-Tos (${numberedShipTos.length})`], ...(hasAction("00082") ? [["contract", "Contract"]] : []), ["audit", "Audit"], ["activity", "Activity"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} className="qw-btn qw-btn-ghost qw-btn-sm"
              style={{ borderBottom: tab === id ? "2px solid #4F46E5" : "2px solid transparent", borderRadius: 0, color: tab === id ? "#4F46E5" : "#64748B" }}>{label}</button>
          ))}
        </div>
      </div>

      {tab === "overview" && (
        <OverviewTab customer={customer} busy={busy}
          onReview={(patch, rows) => setReviewState({ patch, rows })} />
      )}

      {tab === "contacts" && (
        <div className="qw-card qw-pad">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
            <div>
              <div className="qw-eyebrow">Customer contacts</div>
              <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>Add, edit or delete contacts.</p>
            </div>
            {hasAction("00021", true) && <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={busy} onClick={() => setContactModal({ contact: null, shipToNum: null })}>＋ Add contact</button>}
          </div>
          {customerContacts.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 13 }}>No contacts.</div> : (
            <div className="qw-stack">
              {customerContacts.map((ct, i) => (
                <div key={f(ct, "conNum") || i} className="qw-card-flat qw-pad">
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, color: "#0F172A" }}>{f(ct, "name")}</span>
                      <span className="qw-pill qw-pill-slate">SOLD-TO</span>
                      {isPlaceholderContact(ct) && <span className="qw-pill qw-pill-amber">PLACEHOLDER</span>}
                      {f(ct, "primaryPurchasing", false) && <span className="qw-pill qw-pill-indigo">PURCH</span>}
                      {f(ct, "primaryBilling", false) && <span className="qw-pill qw-pill-indigo">BILL</span>}
                      {f(ct, "primaryShipping", false) && <span className="qw-pill qw-pill-indigo">SHIP</span>}
                      {f(ct, "inactive", false) && <span className="qw-pill qw-pill-slate">INACTIVE</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {hasAction("00022", true) && !f(ct, "primaryBilling", false) && !f(ct, "inactive", false) && (
                        <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy || !isEmail(f(ct, "eMailAddress"))}
                          title={isEmail(f(ct, "eMailAddress"))
                            ? "Make this the primary billing contact — invoices and order emails go to them."
                            : "A primary billing contact needs a valid email — add one first (✏ Edit)."}
                          onClick={() => makeBillingRecipient(ct)}>★ Make billing</button>
                      )}
                      {hasAction("00022", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy} onClick={() => setContactModal({ contact: ct, shipToNum: contactShipTo(ct) || null })}>✏ Edit</button>}
                      {hasAction("00023", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy} onClick={() => deleteContact(ct)} style={{ color: "#B91C1C" }}>🗑</button>}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>{f(ct, "contactTitle")}{f(ct, "roleCode") ? ` · ${f(ct, "roleCode")}` : ""}</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>📞 {f(ct, "phoneNum") || "—"}{f(ct, "cellPhoneNum") ? ` · 📱 ${f(ct, "cellPhoneNum")}` : ""} · ✉ {f(ct, "eMailAddress") || "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "shiptos" && (
        <div className="qw-card qw-pad">
          {openShipToObj ? (
            /* Contacts view for ONE ship-to — replaces the grid (a dedicated
               page-like view). "Back to Ship-Tos" returns to the grid. */
            <ShipToContactsPanel
              custNum={custNum}
              shipTo={openShipToObj}
              reloadSignal={ctReload}
              onAdd={() => setContactModal({ contact: null, shipToNum: openShipTo })}
              onEdit={(ct) => setContactModal({ contact: ct, shipToNum: openShipTo })}
              onMutated={async () => { await load(); onChanged && onChanged(); }}
              showToast={toast}
              onError={(m) => toast(m, "err")}
              onBack={() => setOpenShipTo(null)}
            />
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                <div>
                  <div className="qw-eyebrow">Ship-To locations</div>
                  <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>Rep, territory &amp; ship-via are locked from the customer.</p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {hasAction("00027", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy} onClick={normalize}>⚙ Normalize</button>}
                  {hasAction("00024", true) && <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={busy} onClick={() => setShipToModal({})}>＋ Add Ship-To</button>}
                </div>
              </div>
              {numberedShipTos.length === 0 ? <div style={{ color: "#94A3B8", fontSize: 13 }}>No Ship-Tos. The customer's own address is on the Overview tab.</div> : (
                <div className="qw-grid-2">
                  {numberedShipTos.map((st, i) => {
                    const primary = f(st, "primaryShipTo", false);
                    const num = String(f(st, "shipToNum"));
                    const stContacts = contactsForShipTo(num);
                    const needsReceiver = stContacts.length > 0
                      && !stContacts.some((c) => !isPlaceholderContact(c) && !f(c, "inactive", false));
                    return (
                      <div key={f(st, "shipToNum") || i} className="qw-card-flat qw-pad" style={primary ? { boxShadow: "0 0 0 2px #EDE9FE" } : undefined}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 6, marginBottom: 4 }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <span className="qw-mono" style={{ fontWeight: 700, color: "#4F46E5" }}>#{f(st, "shipToNum")}</span>
                            {primary && <span className="qw-pill qw-pill-indigo">PRIMARY</span>}
                            {f(st, "inactive", false) && <span className="qw-pill qw-pill-slate">INACTIVE</span>}
                            {needsReceiver && <span className="qw-pill qw-pill-amber" title="Only a placeholder receiver — confirm the real one.">NEEDS RECEIVER</span>}
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            {hasAction("00025", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy} onClick={() => setShipToModal(st)}>✏</button>}
                            {!primary && hasAction("00026", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={busy} onClick={() => deleteShipTo(st)} style={{ color: "#B91C1C" }}>🗑</button>}
                          </div>
                        </div>
                        <div style={{ fontWeight: 600, color: "#0F172A", fontSize: 13 }}>{f(st, "name")}</div>
                        <div style={{ fontSize: 12, color: "#64748B" }}>{f(st, "address1")}</div>
                        <div style={{ fontSize: 12, color: "#64748B" }}>{f(st, "city")}{f(st, "state") ? `, ${f(st, "state")}` : ""} {f(st, "zip") || f(st, "ZIP")}</div>
                        <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>Rep <span className="qw-mono">{f(st, "salesRepCode") || "—"}</span> · Terr <span className="qw-mono">{f(st, "territoryID") || "—"}</span></div>
                        <button className="qw-btn qw-btn-ghost qw-btn-sm" style={{ marginTop: 8, width: "100%" }} disabled={busy}
                          onClick={() => setOpenShipTo(num)}>
                          👥 Manage contacts ({stContacts.length})
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "contract" && <CustomerContractTab custNum={custNum} custId={f(customer, "custID")} customerName={f(customer, "name")} />}

      {tab === "audit" && <AuditTab custNum={custNum} />}

      {tab === "activity" && <ActivityTab customer={customer} />}

      {reviewState && (
        <ReviewModal
          customer={customer} rows={reviewState.rows} patch={reviewState.patch}
          onClose={() => setReviewState(null)}
          onSubmitted={async () => { setReviewState(null); toast("Customer updated"); await load(); onChanged && onChanged(); }}
          onError={(m) => toast(m, "err")}
        />
      )}

      {contactModal && (
        <ContactModal
          custNum={custNum} contact={contactModal.contact} shipToNum={contactModal.shipToNum}
          otherBillingName={(() => {
            const cur = contacts.find(
              (c) => f(c, "primaryBilling", false)
                && (!contactModal.contact
                  || String(f(c, "conNum")) !== String(f(contactModal.contact, "conNum"))
                  || contactShipTo(c) !== contactShipTo(contactModal.contact)));
            return cur ? f(cur, "name") : null;
          })()}
          onClose={() => setContactModal(null)}
          onSaved={async () => { setContactModal(null); toast("Contact saved"); await load(); setCtReload((n) => n + 1); onChanged && onChanged(); }}
          onError={(m) => toast(m, "err")}
        />
      )}

      {shipToModal && (
        <ShipToModal
          custNum={custNum} customer={customer} shipTo={shipToModal.shipToNum ? shipToModal : null}
          onClose={() => setShipToModal(null)}
          onSaved={async (newShipToNum) => {
            setShipToModal(null); toast("Ship-To saved"); await load();
            // Scenario 1, step 3 — after creating, select the new ship-to so the
            // rep immediately sees its auto-created (real or placeholder) contact.
            if (newShipToNum !== undefined && newShipToNum !== null) { setTab("shiptos"); setOpenShipTo(String(newShipToNum)); }
            onChanged && onChanged();
          }}
          onError={(m) => toast(m, "err")}
        />
      )}
    </div>
  );
}

// ── Overview (editable) ────────────────────────────────────────────────────────
// Overview is a read-out, not a form. Editing happens in a modal opened from the
// Edit button. Previously every field sat inline and always-editable, so there
// was no way to just *read* the customer without parsing a screen of inputs, and
// a stray keystroke anywhere left the tab in a "pending changes" state.
// The review-diff step is unchanged — the modal hands (patch, rows) up exactly
// as the inline form did, and ReviewModal still gates the submit.
// The blank-numbered ShipTo row is the customer's own address, which Epicor
// keeps in step with the customer record — so the Name / Address 1 / City /
// State / ZIP below ARE that row. It briefly had a card of its own here; that
// just printed the same five values twice. It stays off the Ship-Tos tab
// (which lists real delivery addresses) and its contacts are the Sold-To
// contacts on the Contacts tab.
function OverviewTab({ customer, busy, onReview }) {
  const [editOpen, setEditOpen] = useState(false);
  const val = (fd) => disp(norm(pickField(customer, fd.k), fd), fd);

  return (
    <>
    <div className="qw-card qw-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div className="qw-eyebrow">Customer info</div>
          <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>Use Edit to change any of these, then review &amp; submit.</p>
        </div>
        {hasAction("00017", true) && <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={busy} onClick={() => setEditOpen(true)}>✏ Edit</button>}
      </div>

      <div className="qw-grid-auto" style={{ marginTop: 12 }}>
        {OV_CORE.map((fd) => <ReadRow key={fd.k} label={fd.label} span={fd.k === "name"}>{val(fd)}</ReadRow>)}
        <ReadRow label="Territory">
          <span className="qw-mono" style={{ color: "#4F46E5" }}>{deriveTerritory(f(customer, "salesRepCode")) || "—"}</span>
        </ReadRow>
      </div>

      <details style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 12, marginTop: 14 }}>
        <summary className="qw-eyebrow" style={{ cursor: "pointer" }}>Shukla custom (_c) fields</summary>
        <div className="qw-grid-auto" style={{ marginTop: 12 }}>
          {OV_CUSTOM.map((fd) => <ReadRow key={fd.k} label={fd.label}>{val(fd)}</ReadRow>)}
        </div>
      </details>
    </div>

    {editOpen && (
      <CustomerEditModal
        customer={customer}
        onClose={() => setEditOpen(false)}
        // Close first so the review diff isn't stacked behind this modal.
        onReview={(patch, rows) => { setEditOpen(false); onReview(patch, rows); }}
      />
    )}
    </>
  );
}

// ── Edit customer info modal → hands a diff up to ReviewModal ─────────────────
// Holds the same field set the Overview tab used to render inline; opens
// prepopulated from the loaded customer.
function CustomerEditModal({ customer, onClose, onReview }) {
  const [edits, setEdits] = useState(() => editsFromCustomer(customer));
  useEffect(() => { setEdits(editsFromCustomer(customer)); }, [customer]);

  const set = (k, v) => setEdits((p) => ({ ...p, [k]: v }));
  const repCfg = useMemo(() => ({ ...buildSalesRepConfig(), hideSecondary: true }), []);
  const { rows, patch } = computeDiff(customer, edits);

  return (
    <Modal show onHide={onClose} centered scrollable size="lg" contentClassName="qw-root">
      <Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>Edit customer info</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="qw-grid-2">
          <TextField label="Name *" span value={edits.name} onChange={(v) => set("name", v)} />
          <div>
            <label className="qw-label">Customer Type</label>
            <select className="qw-input" value={edits.customerType} onChange={(e) => set("customerType", e.target.value)}>{["CUS", "PRO", "SUS"].map((x) => <option key={x}>{x}</option>)}</select>
          </div>
          <TextField label="Legal Name" value={edits.legalName} onChange={(v) => set("legalName", v)} />
          <div style={{ gridColumn: "1 / -1", position: "relative", zIndex: 5 }}>
            <label className="qw-label">Address 1</label>
            <AddressAutocompleteField
              config={ADDRESS_AUTOCOMPLETE_CONFIG}
              value={edits.address1}
              onChange={(v) => set("address1", v)}
              onSelectDetails={(det) => det && setEdits((p) => ({ ...p, address1: det.address1 ?? p.address1, city: det.city ?? p.city, state: det.state ?? p.state, zip: det.zipCode ?? det.zip ?? p.zip }))}
              className="qw-input"
              placeholder="Start typing an address…"
            />
          </div>
          <TextField label="City" value={edits.city} onChange={(v) => set("city", v)} />
          <div className="qw-grid-2" style={{ gap: 12 }}>
            <TextField label="State" value={edits.state} onChange={(v) => set("state", v)} />
            <TextField label="ZIP" value={edits.zip} onChange={(v) => set("zip", v)} />
          </div>
          <TextField label="Phone" value={edits.phoneNum} onChange={(v) => set("phoneNum", v)} />
          <TextField label="Email" value={edits.eMailAddress} onChange={(v) => set("eMailAddress", v)} />
          <div>
            <label className="qw-label">Sales Rep</label>
            <SearchableSelect
              config={repCfg}
              value={edits.salesRepCode}
              onChange={(v) => set("salesRepCode", v)}
              onSelectItem={(it) => set("salesRepCode", pickField(it, "salesRepCode") || "")}
              placeholder="Type code or name…"
              inputWidth="100%"
            />
            <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>Territory → <span className="qw-mono" style={{ color: "#4F46E5" }}>{deriveTerritory(edits.salesRepCode) || "—"}</span></div>
          </div>
          <div>
            <label className="qw-label">Terms</label>
            <select className="qw-input" value={edits.termsCode} onChange={(e) => set("termsCode", e.target.value)}>{["N30", "COD", "CIA", "N60"].map((x) => <option key={x}>{x}</option>)}</select>
          </div>
          <TextField label="Credit Limit" type="number" value={edits.creditLimit} onChange={(v) => set("creditLimit", v)} />
          <div style={{ display: "flex", gap: 18, alignItems: "center", paddingTop: 22 }}>
            <CheckField label="Inactive" checked={edits.inactive} onChange={(v) => set("inactive", v)} />
          </div>
        </div>

        <details style={{ border: "1px solid #E2E8F0", borderRadius: 10, padding: 12, marginTop: 14 }}>
          <summary className="qw-eyebrow" style={{ cursor: "pointer" }}>Shukla custom (_c) fields</summary>
          <div className="qw-grid-2" style={{ marginTop: 12 }}>
            <TextField label="Hospital Type" value={edits.hospitalType_c} onChange={(v) => set("hospitalType_c", v)} />
            <div className="qw-grid-2" style={{ gap: 12 }}>
              <TextField label="No. of Beds" type="number" value={edits.noOfBeds_c} onChange={(v) => set("noOfBeds_c", v)} />
              <TextField label="Trauma Level" type="number" value={edits.traumaLevel_c} onChange={(v) => set("traumaLevel_c", v)} />
            </div>
            <div style={{ display: "flex", gap: 18, alignItems: "center", paddingTop: 22 }}>
              <CheckField label="Helipad" checked={edits.helipad_c} onChange={(v) => set("helipad_c", v)} />
              {/* "Has Contract" is DISPLAY-ONLY now. It is an Epicor custom field and nothing in
                  our backend reads it to make a decision — all twelve references are plumbing.
                  Editing it here made it a second answer to a question the Contract tab already
                  answers from real contract rows, and the two would disagree the first time
                  somebody forgot to tick it. Still shown on the overview, because Kinetic users
                  see the field and hiding it from this screen would not un-set it.
                  Deliberately NOT auto-synced from our data: it belongs to another system, and
                  writing it from here is how you get a loop. */}
            </div>
          </div>
        </details>

        {rows.length > 0 && (
          <div className="qw-info qw-info-amber" style={{ marginTop: 14, background: "#FFFBEB", color: "#92400E" }}>
            <div className="qw-eyebrow" style={{ marginBottom: 6 }}>Pending changes</div>
            {rows.map((r) => (
              <div key={r.k} style={{ fontSize: 12, marginBottom: 2 }}>
                <strong>{r.label}:</strong> <span style={{ color: "#94A3B8" }}>{r.oldDisp}</span> → <span style={{ color: "#0F172A", fontWeight: 600 }}>{r.newDisp}</span>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={rows.length === 0} onClick={() => setEdits(editsFromCustomer(customer))}>Revert</button>
        <button className="qw-btn qw-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-primary" disabled={rows.length === 0} onClick={() => onReview(patch, rows)}>Review &amp; submit →</button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Review & submit (customer diff) modal ──────────────────────────────────────
function ReviewModal({ customer, rows, patch, onClose, onSubmitted, onError }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    setSubmitting(true);
    try { await patchCustomerFull(f(customer, "custNum"), patch); await onSubmitted(); }
    catch (e) { onError(errorText(e, "Couldn't update the customer.")); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal show onHide={submitting ? undefined : onClose} centered scrollable contentClassName="qw-root">
      <Modal.Header closeButton={!submitting}><Modal.Title style={{ fontSize: 18 }}>Review changes · {f(customer, "custID")}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="qw-sub" style={{ fontSize: 12 }}>Only the changed fields are submitted.</p>
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse", marginTop: 8 }}>
          <thead><tr style={{ textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase" }}><th style={{ padding: "6px 8px" }}>Field</th><th style={{ padding: "6px 8px" }}>Old</th><th style={{ padding: "6px 8px" }}>New</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.k} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "6px 8px", fontWeight: 600 }}>{r.label}</td>
                <td style={{ padding: "6px 8px", color: "#94A3B8" }}>{r.oldDisp}</td>
                <td style={{ padding: "6px 8px", color: "#0F172A", fontWeight: 600 }}>{r.newDisp}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Modal.Body>
      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={submitting} onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-primary" disabled={submitting || rows.length === 0} onClick={submit}>{submitting ? "Submitting…" : "Submit changes"}</button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Contact add/edit modal ─────────────────────────────────────────────────────
// Create: ship-to-scoped → POST /api/CustCnt (with shipToNum); customer-level →
// POST /api/CustCnt/full. Edit: PATCH /api/CustCnt/full/{cust}/{shipTo}/{con}.
function ContactModal({ custNum, contact, shipToNum, onClose, onSaved, onError, otherBillingName }) {
  const editing = !!contact;
  // A non-null shipToNum (including "0") means this contact is tied to that
  // delivery address rather than the customer as a whole.
  const scoped = shipToNum !== null && shipToNum !== undefined;
  const [d, setD] = useState(() => ({
    name: f(contact, "name"), firstName: f(contact, "firstName"), lastName: f(contact, "lastName"),
    roleCode: f(contact, "roleCode", scoped ? "ShpRec" : "Purch"), contactTitle: f(contact, "contactTitle", scoped && !editing ? "Receiver" : ""),
    phoneNum: f(contact, "phoneNum"), cellPhoneNum: f(contact, "cellPhoneNum"), eMailAddress: f(contact, "eMailAddress"),
    primaryPurchasing: !!f(contact, "primaryPurchasing", false), primaryBilling: !!f(contact, "primaryBilling", false),
    primaryShipping: !!f(contact, "primaryShipping", false), inactive: !!f(contact, "inactive", false),
  }));
  const [saving, setSaving] = useState(false);
  // Role codes loaded from Epicor (so we never offer an invalid one).
  const [roleOptions, setRoleOptions] = useState(ROLE_FALLBACK);
  useEffect(() => {
    let alive = true;
    fetchRoleCodes().then((c) => { if (alive) setRoleOptions(c); });
    return () => { alive = false; };
  }, []);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const onName = (v) => { const { firstName, lastName } = splitName(v); setD((p) => ({ ...p, name: v, firstName: p.firstName || firstName, lastName: p.lastName || lastName })); };
  // Keep the current value selectable even if it's not in the fetched list.
  const roleChoices = d.roleCode && !roleOptions.includes(d.roleCode) ? [d.roleCode, ...roleOptions] : roleOptions;
  const valid = d.name.trim() && d.roleCode.trim() && d.contactTitle.trim() && d.phoneNum.trim() && d.eMailAddress.trim();

  const save = async () => {
    if (!valid) { onError("Fill name, role, title, phone and email."); return; }
    if (d.primaryBilling) {
      // Server rule: the billing recipient must have a valid email — invoices
      // and order communication resolve it live at send.
      if (!isEmail(d.eMailAddress)) { onError("A primary billing contact needs a valid email address."); return; }
      // Only ONE contact can be primary billing — turning it on displaces the
      // current holder (server clears the flag on every other contact).
      if (!f(contact, "primaryBilling", false) && otherBillingName
          && !window.confirm(`${otherBillingName} is currently the primary billing contact. Make this contact the primary billing contact instead?`)) return;
    }
    setSaving(true);
    try {
      if (editing) {
        const shipToNum = shipToSeg(f(contact, "shipToNum"));
        const body = {
          name: d.name, firstName: d.firstName, lastName: d.lastName, contactTitle: d.contactTitle, roleCode: d.roleCode,
          phoneNum: d.phoneNum, cellPhoneNum: d.cellPhoneNum, eMailAddress: d.eMailAddress, inactive: d.inactive,
          primaryPurchasing: d.primaryPurchasing, primaryBilling: d.primaryBilling, primaryShipping: d.primaryShipping, updatedBy: me(),
        };
        const r = await apiFetch(`${API_URL}/api/CustCnt/full/${custNum}/${encodeURIComponent(shipToNum)}/${f(contact, "conNum")}`, { method: "PATCH", body: JSON.stringify(body) });
        const res = await r.json().catch(() => ({}));
        if (!r.ok || res.success === false) throw new Error(humanError(r, res, "Couldn't save the contact changes."));
      } else if (scoped) {
        // Ship-to-scoped create — ties the contact to the delivery address so an
        // order can ship there. Epicor keys contacts by (cust, shipTo, conNum).
        const body = {
          custNum: Number(custNum), shipToNum: String(shipToNum),
          name: d.name, firstName: d.firstName, lastName: d.lastName, roleCode: d.roleCode, contactTitle: d.contactTitle,
          phoneNum: d.phoneNum, cellPhoneNum: d.cellPhoneNum, eMailAddress: d.eMailAddress,
          primaryPurchasing: d.primaryPurchasing, primaryBilling: d.primaryBilling, primaryShipping: d.primaryShipping,
        };
        const r = await apiFetch(`${API_URL}/api/CustCnt`, { method: "POST", body: JSON.stringify(body) });
        const res = await r.json().catch(() => ({}));
        if (!r.ok || res.success === false) throw new Error(humanError(r, res, "Couldn't add the contact."));
      } else {
        const body = {
          custNum: Number(custNum), name: d.name, firstName: d.firstName, lastName: d.lastName, roleCode: d.roleCode, contactTitle: d.contactTitle,
          phoneNum: d.phoneNum, cellPhoneNum: d.cellPhoneNum, eMailAddress: d.eMailAddress,
          primaryPurchasing: d.primaryPurchasing, primaryBilling: d.primaryBilling, primaryShipping: d.primaryShipping, createdBy: me(),
        };
        const r = await apiFetch(`${API_URL}/api/CustCnt/full`, { method: "POST", body: JSON.stringify(body) });
        const res = await r.json().catch(() => ({}));
        if (!r.ok || res.success === false) throw new Error(humanError(r, res, "Couldn't add the contact."));
      }
      await onSaved();
    } catch (e) { onError(errorText(e, "Couldn't save your changes.")); }
    finally { setSaving(false); }
  };

  return (
    <Modal show onHide={saving ? undefined : onClose} centered scrollable contentClassName="qw-root">
      <Modal.Header closeButton={!saving}><Modal.Title style={{ fontSize: 18 }}>{editing ? `Edit contact · ConNum ${f(contact, "conNum")}` : scoped ? `Add contact · Ship-To #${shipToNum}` : "Add contact"}</Modal.Title></Modal.Header>
      <Modal.Body>
        {scoped && (
          <div className="qw-info qw-info-amber" style={{ marginBottom: 12, background: "#EEF2FF", color: "#3730A3", fontSize: 12 }}>
            This contact is tied to <strong>Ship-To #{shipToNum}</strong> — the person who receives deliveries there.
            Turn on <strong>Primary shipping</strong> to make them the default receiver Epicor stamps on orders.
          </div>
        )}
        <div className="qw-grid-2">
          <div style={{ gridColumn: "1 / -1" }}>
            <label className="qw-label">Full name *</label>
            <input className="qw-input" value={d.name} onChange={(e) => onName(e.target.value)} />
          </div>
          <TextField label="First name" value={d.firstName} onChange={(v) => set("firstName", v)} />
          <TextField label="Last name" value={d.lastName} onChange={(v) => set("lastName", v)} />
          <div>
            <label className="qw-label">Role code *</label>
            <select className="qw-input qw-mono" value={d.roleCode} onChange={(e) => set("roleCode", e.target.value)}>{roleChoices.map((x) => <option key={x}>{x}</option>)}</select>
          </div>
          <TextField label="Title *" value={d.contactTitle} onChange={(v) => set("contactTitle", v)} />
          <TextField label="Office phone *" value={d.phoneNum} onChange={(v) => set("phoneNum", v)} />
          <TextField label="Cell phone" value={d.cellPhoneNum} onChange={(v) => set("cellPhoneNum", v)} />
          <TextField label="Email *" span value={d.eMailAddress} onChange={(v) => set("eMailAddress", v)} />
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12 }}>
          <CheckField label="Primary purchasing" checked={d.primaryPurchasing} onChange={(v) => set("primaryPurchasing", v)} />
          <CheckField label="Primary billing" checked={d.primaryBilling} onChange={(v) => set("primaryBilling", v)} />
          <CheckField label="Primary shipping" checked={d.primaryShipping} onChange={(v) => set("primaryShipping", v)} />
          {editing && <CheckField label="Inactive" checked={d.inactive} onChange={(v) => set("inactive", v)} />}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : editing ? "Save contact" : "Add contact"}</button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Per-ship-to contacts panel ─────────────────────────────────────────────────
// Master-detail companion to the Ship-Tos grid: the contacts that receive
// deliveries at ONE ship-to. Every ship-to always has ≥1 contact (backend
// invariant); a lone placeholder is badged and the rep is nudged to confirm the
// real receiver. Mirrors the ship-to-contacts simulator's right-hand panel.
function ShipToContactsPanel({ custNum, shipTo, reloadSignal, onAdd, onEdit, onMutated, showToast, onError, onBack }) {
  const num = String(f(shipTo, "shipToNum"));
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acting, setActing] = useState(false);

  // Simulator "open contacts for a ship-to": GET /api/CustCnt?custNum=&shipToNum={s}.
  // The ship-to segment is required because ConNum is unique only within a ship-to.
  const loadList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/CustCnt?custNum=${custNum}&shipToNum=${encodeURIComponent(num)}`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't load this ship-to's contacts."));
      setList(d.contacts || d.Contacts || []);
    } catch (e) { setError(errorText(e, "Couldn't load contacts.")); setList([]); }
    finally { setLoading(false); }
  }, [custNum, num]);

  useEffect(() => { loadList(); }, [loadList, reloadSignal]);

  // Simulator "edit / make-default / retire": PATCH /api/CustCnt/full/{cust}/{ship}/{con}.
  const patchFull = async (ct, patch, okMsg) => {
    setActing(true);
    try {
      const r = await apiFetch(`${API_URL}/api/CustCnt/full/${custNum}/${encodeURIComponent(shipToSeg(num))}/${f(ct, "conNum")}`,
        { method: "PATCH", body: JSON.stringify({ ...patch, updatedBy: me() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't update this contact."));
      showToast && showToast(okMsg);
      await loadList(); onMutated && onMutated();
    } catch (e) { onError && onError(errorText(e, "Couldn't update this contact.")); }
    finally { setActing(false); }
  };
  const makePrimary = (ct) => patchFull(ct, { primaryShipping: true }, "Set as default receiver");
  const retire = (ct) => {
    if (!window.confirm(`Retire "${f(ct, "name")}"? They'll be hidden from new orders but kept on existing ones.`)) return;
    patchFull(ct, { inactive: true }, "Contact retired");
  };

  // Simulator "delete a ship-to contact": DELETE /api/CustCnt/{cust}/{ship}/{con}.
  const del = async (ct) => {
    if (list.filter((c) => !f(c, "inactive", false)).length <= 1 && !f(ct, "inactive", false)) {
      if (!window.confirm(`"${f(ct, "name")}" is the only active contact at Ship-To #${num}. A ship-to needs a receiver to ship orders — delete anyway?`)) return;
    } else if (!window.confirm(`Delete contact "${f(ct, "name")}" at Ship-To #${num}?`)) return;
    setActing(true);
    try {
      const r = await apiFetch(`${API_URL}/api/CustCnt/${custNum}/${encodeURIComponent(shipToSeg(num))}/${f(ct, "conNum")}`, { method: "DELETE" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Couldn't delete this contact."));
      showToast && showToast("Contact deleted");
      await loadList(); onMutated && onMutated();
    } catch (e) { onError && onError(errorText(e, "Couldn't delete this contact.")); }
    finally { setActing(false); }
  };

  const disabled = acting;
  const hasRealReceiver = list.some((c) => !isPlaceholderContact(c) && !f(c, "inactive", false));
  const needsReceiver = list.length > 0 && !hasRealReceiver;

  return (
    <div>
      <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={disabled} onClick={onBack} style={{ marginBottom: 12 }}>
        <FaChevronLeft /> Back to Ship-Tos
      </button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div>
          <div className="qw-eyebrow">Contacts at Ship-To #{num}</div>
          <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>{f(shipTo, "name")} — who receives deliveries here.</p>
        </div>
        {hasAction("00028", true) && <button className="qw-btn qw-btn-primary qw-btn-sm" disabled={disabled} onClick={onAdd}>＋ Add contact</button>}
      </div>
      {loading && <div style={{ color: "#64748B", fontSize: 13 }}><Spinner animation="border" size="sm" /> Loading contacts…</div>}
      {error && <div className="qw-info qw-info-amber" style={{ marginBottom: 10, background: "#FEF2F2", color: "#991B1B", fontSize: 12 }}>{error}</div>}
      {!loading && !error && (
        <>
          {needsReceiver && (
            <div className="qw-info qw-info-amber" style={{ marginBottom: 10, background: "#FFFBEB", color: "#92400E", fontSize: 12 }}>
              This ship-to only has a <strong>placeholder</strong> receiver. Confirm the real receiver — edit the placeholder
              (set their name and mark them the default receiver) or add a new contact — so orders and confirmation emails go to the right person.
            </div>
          )}
          {list.length === 0 ? (
            <div style={{ color: "#94A3B8", fontSize: 13 }}>No contacts at this ship-to yet. Add one so orders can ship here.</div>
          ) : (
            <div className="qw-stack">
              {list.map((ct, i) => {
                const ph = isPlaceholderContact(ct);
                const primary = f(ct, "primaryShipping", false);
                const inact = f(ct, "inactive", false);
                return (
                  <div key={f(ct, "conNum") || i} className="qw-card-flat qw-pad">
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, color: "#0F172A" }}>{f(ct, "name")}</span>
                        {ph ? <span className="qw-pill qw-pill-amber">PLACEHOLDER</span>
                          : primary ? <span className="qw-pill qw-pill-emerald">DEFAULT RECEIVER</span> : null}
                        {inact && <span className="qw-pill qw-pill-slate">INACTIVE</span>}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {/* 00029 edit (incl. confirm / make default / retire), 00030 delete */}
                        {ph && hasAction("00029", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={disabled} onClick={() => onEdit(ct)} style={{ color: "#92400E" }}>✔ Confirm receiver</button>}
                        {!ph && !primary && !inact && hasAction("00029", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={disabled} onClick={() => makePrimary(ct)}>★ Make default</button>}
                        {hasAction("00029", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={disabled} onClick={() => onEdit(ct)}>✏</button>}
                        {!inact && hasAction("00029", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={disabled} onClick={() => retire(ct)} title="Retire — hide from new orders, keep on existing ones">💤</button>}
                        {hasAction("00030", true) && <button className="qw-btn qw-btn-ghost qw-btn-sm" disabled={disabled} onClick={() => del(ct)} style={{ color: "#B91C1C" }}>🗑</button>}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>{f(ct, "contactTitle")}{f(ct, "roleCode") ? ` · ${f(ct, "roleCode")}` : ""}</div>
                    <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>📞 {f(ct, "phoneNum") || "—"} · ✉ {f(ct, "eMailAddress") || "—"}</div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Ship-To add/edit modal → /api/ShipTo ───────────────────────────────────────
function ShipToModal({ custNum, customer, shipTo, onClose, onSaved, onError }) {
  const editing = !!shipTo;
  const [d, setD] = useState(() => ({
    name: f(shipTo, "name"), address1: f(shipTo, "address1"), address2: f(shipTo, "address2"),
    city: f(shipTo, "city"), state: f(shipTo, "state"), zip: f(shipTo, "zip") || f(shipTo, "ZIP"), country: f(shipTo, "country", "USA"),
    phoneNum: f(shipTo, "phoneNum"), eMailAddress: f(shipTo, "eMailAddress"), contactName: f(shipTo, "contactName"),
    primaryShipTo: !!f(shipTo, "primaryShipTo", false), inactive: !!f(shipTo, "inactive", false),
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const valid = d.name.trim() && d.address1.trim() && d.city.trim() && d.state.trim() && String(d.zip).trim();

  const save = async () => {
    if (!valid) { onError("Fill name, address, city, state and ZIP."); return; }
    setSaving(true);
    try {
      if (editing) {
        const body = {
          name: d.name, address1: d.address1, address2: d.address2, city: d.city, state: d.state, zip: d.zip, country: d.country,
          phoneNum: d.phoneNum, eMailAddress: d.eMailAddress, contactName: d.contactName, primaryShipTo: d.primaryShipTo, inactive: d.inactive,
        };
        const r = await apiFetch(`${API_URL}/api/ShipTo/${custNum}/${encodeURIComponent(f(shipTo, "shipToNum"))}`, { method: "PATCH", body: JSON.stringify(body) });
        const res = await r.json().catch(() => ({}));
        if (!r.ok || res.success === false) throw new Error(humanError(r, res, "Couldn't save the ship-to changes."));
        await onSaved();
        // DEF-14: the address saves even when Epicor refuses the primary move — say so.
        if (d.primaryShipTo && !f(res.shipTo, "primaryShipTo", false)) onError(primaryNotSetText(res));
      } else {
        const body = {
          custNum: Number(custNum), name: d.name, address1: d.address1, address2: d.address2 || null,
          city: d.city, state: d.state, zip: d.zip, country: d.country,
          phoneNum: d.phoneNum || null, eMailAddress: d.eMailAddress || null, contactName: d.contactName || null,
          primaryShipTo: !!d.primaryShipTo,
        };
        const r = await apiFetch(`${API_URL}/api/ShipTo`, { method: "POST", body: JSON.stringify(body) });
        const res = await r.json().catch(() => ({}));
        if (!r.ok || res.success === false) throw new Error(humanError(r, res, "Couldn't add the ship-to."));
        // Hand the new ShipToNum back so the caller can select it (auto-open its contacts).
        await onSaved(res.shipToNum ?? res.ShipToNum);
        if (d.primaryShipTo && !f(res, "isPrimary", false)) onError(primaryNotSetText(res));
      }
    } catch (e) { onError(errorText(e, "Couldn't save your changes.")); }
    finally { setSaving(false); }
  };

  const rep = f(customer, "salesRepCode");
  return (
    <Modal show onHide={saving ? undefined : onClose} centered scrollable contentClassName="qw-root">
      <Modal.Header closeButton={!saving}><Modal.Title style={{ fontSize: 18 }}>{editing ? `Edit Ship-To #${f(shipTo, "shipToNum")}` : "Add Ship-To"}</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="qw-grid-2">
          <TextField label="Location name *" span value={d.name} onChange={(v) => set("name", v)} />
          <div style={{ gridColumn: "1 / -1", position: "relative", zIndex: 5 }}>
            <label className="qw-label">Address 1 *</label>
            <AddressAutocompleteField
              config={ADDRESS_AUTOCOMPLETE_CONFIG}
              value={d.address1}
              onChange={(v) => set("address1", v)}
              onSelectDetails={(det) => det && setD((p) => ({ ...p, address1: det.address1 ?? p.address1, address2: det.address2 ?? p.address2, city: det.city ?? p.city, state: det.state ?? p.state, zip: det.zipCode ?? det.zip ?? p.zip }))}
              className="qw-input"
              placeholder="Start typing an address…"
            />
          </div>
          <TextField label="Address 2" span value={d.address2} onChange={(v) => set("address2", v)} />
          <TextField label="City *" value={d.city} onChange={(v) => set("city", v)} />
          <div className="qw-grid-2" style={{ gap: 12 }}>
            <TextField label="State *" value={d.state} onChange={(v) => set("state", v)} />
            <TextField label="ZIP *" value={d.zip} onChange={(v) => set("zip", v)} />
          </div>
          <div>
            <label className="qw-label">Country</label>
            <select className="qw-input" value={d.country} onChange={(e) => set("country", e.target.value)}><option>USA</option><option>CAN</option></select>
          </div>
          <TextField label="Phone" value={d.phoneNum} onChange={(v) => set("phoneNum", v)} />
          <TextField label="Email" value={d.eMailAddress} onChange={(v) => set("eMailAddress", v)} />
          <div>
            <label className="qw-label">{editing ? "Contact name" : "Receiver contact name"}</label>
            <input className="qw-input" value={d.contactName ?? ""} onChange={(e) => set("contactName", e.target.value)} />
            {!editing && (
              <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 4 }}>
                Recommended — creates the real receiver contact for this address. Leave blank and a placeholder receiver is created for you to confirm later.
              </div>
            )}
          </div>
          <div>
            <label className="qw-label">Sales Rep (locked)</label>
            <input className="qw-input qw-mono" value={rep || ""} disabled readOnly />
          </div>
          <div>
            <label className="qw-label">Territory (locked)</label>
            <input className="qw-input qw-mono" value={f(customer, "territoryID") || deriveTerritory(rep) || ""} disabled readOnly />
          </div>
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 12 }}>
          <CheckField label="Primary Ship-To" checked={d.primaryShipTo} onChange={(v) => set("primaryShipTo", v)} />
          {editing && <CheckField label="Inactive" checked={d.inactive} onChange={(v) => set("inactive", v)} />}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="qw-btn qw-btn-ghost" disabled={saving} onClick={onClose}>Cancel</button>
        <button className="qw-btn qw-btn-primary" disabled={saving} onClick={save}>{saving ? "Saving…" : editing ? "Save Ship-To" : "Add Ship-To"}</button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Activity tab — customer activity, entity-generic ──────────────────────────
// One tab, many activity types: Quotes / Orders / Shipments today, and any
// future entity (Invoices, Payments, …) is ONE more ACTIVITY_ENTITIES entry —
// no layout or handler changes. Each entry declares:
//   • route + toPageFilters: Search navigates to the entity's list page with
//     the filters pre-applied via router state (each page's initialFilters
//     support), scoped to THIS customer;
//   • buildQs/dataKey/lastField/nextCursor: Download cursor-walks the entity's
//     API (100 rows/page, via the pages' own exported query builders) into a
//     CSV;
//   • fields/statusOptions: which shared filter controls the entity offers
//     (only filters its API actually honours — e.g. entry dates exist only on
//     quotes; orders/shipments use a server-side date window).
const ACTIVITY_DAY = (v) => (v ? String(v).slice(0, 10) : "");

const ACTIVITY_ENTITIES = [
  {
    key: "quotes", label: "Quotes", route: "/quote",
    apiPath: "/api/Quote", dataKey: "quotes", lastField: "lastQuoteNum",
    fields: ["salesRepCode", "territoryID", "status", "dateRange"],
    statusOptions: [
      { label: "Any status", value: "" },
      { label: "Open",       value: "open" },
      { label: "Quoted",     value: "quoted" },
      { label: "Ordered",    value: "ordered" },
      { label: "Expired",    value: "expired" },
      { label: "Closed",     value: "closed" },
    ],
    toPageFilters: (custID, f) => ({
      custID,
      salesRepCode: f.salesRepCode, territoryID: f.territoryID,
      status: f.status, expired: "",
      entryDateFrom: f.dateFrom, entryDateTo: f.dateTo,
      amountFrom: "", amountTo: "",
    }),
    buildQs: (filters, page, cursor) =>
      buildQuoteSearchQs({ filters, page, pageSize: 100, cursor, quoteNumFind: "" }),
    nextCursor: (data) => ({ dir: "next", quoteNum: data.lastQuoteNum }),
    csvHeader: ["Quote #", "Customer ID", "Customer", "Sales Rep", "Rep Name", "Territory", "Entry Date", "Expires", "Total", "Status"],
    csvRow: (q, ctx) => [
      q.quoteNum, q.customerCustID || ctx.custID, q.customerName || ctx.custName,
      q.salesRepCode, q.salesRepName, q.territoryID,
      ACTIVITY_DAY(q.entryDate), ACTIVITY_DAY(q.expirationDate), q.quoteAmt,
      q.quoteClosed ? "Closed" : q.expired ? "Expired" : q.ordered ? "Ordered" : q.quoted ? "Quoted" : "Open",
    ],
  },
  {
    key: "orders", label: "Orders", route: "/orders",
    apiPath: "/api/sales-orders", dataKey: "orders", lastField: "lastOrderNum",
    fields: ["salesRepCode", "status", "poNum"],
    statusOptions: [
      { label: "Any status", value: "" },
      { label: "Open",       value: "open" },
      { label: "Held",       value: "held" },
      { label: "Closed",     value: "closed" },
    ],
    toPageFilters: (custID, f) => ({
      custID, salesRepCode: f.salesRepCode, territoryID: "",
      status: f.status, poNum: f.poNum || "",
    }),
    buildQs: (filters, page, cursor) =>
      buildOrderSearchQs({ filters, page, pageSize: 100, cursor, orderNumFind: "" }),
    nextCursor: (data) => ({ dir: "next", orderNum: data.lastOrderNum }),
    csvHeader: ["Order #", "Customer ID", "Customer", "PO #", "Sales Reps", "Order Date", "Total", "Status", "Shipped", "Invoiced"],
    csvRow: (o, ctx) => [
      o.orderNum, o.customerCustID || ctx.custID, o.customerName || ctx.custName,
      o.poNum, o.salesRepList, ACTIVITY_DAY(o.orderDate), o.docOrderAmt,
      o.orderHeld ? "Held" : o.openOrder ? "Open" : "Closed",
      o.shippedStatus, o.invoicedStatus,
    ],
  },
  {
    key: "shipments", label: "Shipments", route: "/shipments",
    apiPath: "/api/shipments", dataKey: "shipments", lastField: "lastPackNum",
    fields: ["status", "invoiced", "orderNum"],
    statusOptions: [
      { label: "Any status", value: "" },
      { label: "Shipped",    value: "shipped" },
      { label: "Staged",     value: "staged" },
      { label: "Voided",     value: "voided" },
    ],
    toPageFilters: (custID, f) => ({
      custID, status: f.status, invoiced: f.invoiced || "",
      trackingNumber: "", orderNum: f.orderNum || "",
    }),
    buildQs: (filters, page, cursor) =>
      buildShipmentSearchQs({ filters, page, pageSize: 100, cursor, packNumFind: "" }),
    nextCursor: (data) => ({ dir: "next", packNum: data.lastPackNum }),
    csvHeader: ["Pack #", "Order #", "Customer ID", "Customer", "Ship Date", "Status", "Ship Via", "Tracking #", "Invoiced", "Total"],
    csvRow: (s, ctx) => [
      s.packNum, s.orderNum, s.customerCustID || ctx.custID, s.customerName || ctx.custName,
      ACTIVITY_DAY(s.shipDate), s.shipStatus,
      s.shipViaCodeDescription || s.shipVia || s.shipViaCode,
      s.trackingNumber, s.invoiced ? "Yes" : "No", s.docOrderAmt,
    ],
  },
];

const ACTIVITY_INVOICED_OPTIONS = [
  { label: "Any invoicing", value: "" },
  { label: "Invoiced",      value: "yes" },
  { label: "Not invoiced",  value: "no" },
];

const ACTIVITY_BLANK_FILTERS = {
  salesRepCode: "", territoryID: "", status: "",
  dateFrom: "", dateTo: "", poNum: "", invoiced: "", orderNum: "",
};

function ActivityTab({ customer }) {
  const navigate = useNavigate();
  const custID = String(f(customer, "custID") || "");
  const custName = f(customer, "name") || "";

  const [entityKey, setEntityKey] = useState(ACTIVITY_ENTITIES[0].key);
  const entity = ACTIVITY_ENTITIES.find((e) => e.key === entityKey) || ACTIVITY_ENTITIES[0];

  const [filt, setFilt] = useState(ACTIVITY_BLANK_FILTERS);
  const [downloading, setDownloading] = useState(false);
  const [notice, setNotice] = useState(null); // {kind:'ok'|'info'|'err', msg}

  const salesRepConfig = useMemo(() => buildSalesRepConfig(filt.territoryID), [filt.territoryID]);
  const set = (k, v) => { setFilt((p) => ({ ...p, [k]: v })); setNotice(null); };
  const pickEntity = (key) => { setEntityKey(key); setFilt(ACTIVITY_BLANK_FILTERS); setNotice(null); };

  const onSearch = () =>
    navigate(entity.route, { state: { initialFilters: entity.toPageFilters(custID, filt) } });

  const onDownload = async () => {
    setDownloading(true);
    setNotice(null);
    try {
      // Cursor-walk ALL pages (100 rows each; the same live path the list
      // pages use). Safety-capped so a runaway result set can't spin forever —
      // the cap is reported if hit.
      const MAX_PAGES = 50;
      const filters = entity.toPageFilters(custID, filt);
      const all = [];
      let page = 1, cursor = null, capped = false;
      for (;;) {
        const qs = entity.buildQs(filters, page, cursor);
        const r = await apiFetch(`${API_URL}${entity.apiPath}?${qs}`);
        const data = await r.json().catch(() => ({}));
        if (!r.ok || data.success === false) throw new Error(data.error || data.message || `HTTP ${r.status}`);
        all.push(...(Array.isArray(data[entity.dataKey]) ? data[entity.dataKey] : []));
        const more = data.hasMore === true && data[entity.lastField] != null;
        if (!more) break;
        if (page >= MAX_PAGES) { capped = true; break; }
        cursor = entity.nextCursor(data);
        page += 1;
      }

      if (all.length === 0) {
        setNotice({ kind: "info", msg: `No ${entity.label.toLowerCase()} to download for this customer with the selected filters.` });
        return;
      }

      const ctx = { custID, custName };
      // csvCell also stops a cell such as "=HYPERLINK(...)" running as an Excel formula.
      const rows = all.map((row) => entity.csvRow(row, ctx).map(csvCell).join(","));
      // BOM so Excel opens it as UTF-8.
      const blob = new Blob(["﻿" + [entity.csvHeader.join(","), ...rows].join("\r\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity.key}_${custID || "customer"}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setNotice({
        kind: "ok",
        msg: `Downloaded ${all.length} row${all.length === 1 ? "" : "s"} of ${entity.label.toLowerCase()}.` +
             (capped ? " (Result capped — narrow the filters for the rest.)" : ""),
      });
    } catch (e) {
      setNotice({ kind: "err", msg: errorText(e, "Download failed.") });
    } finally {
      setDownloading(false);
    }
  };

  const has = (field) => entity.fields.includes(field);

  // Rendered with the same filter-panel system as the Quotes page
  // (.dyn-qw-filters grid in DynamicQw.css) so the two screens look identical:
  // uppercase labels, 36px rounded fields, responsive 12-track grid.
  return (
    <div className="qw-card dyn-qw" style={{ padding: "18px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <div>
          <div className="qw-eyebrow">Activity</div>
          <p className="qw-sub" style={{ margin: "2px 0 0", fontSize: 12 }}>
            Search or download <strong>{custName || custID}</strong>'s {entity.label.toLowerCase()}.
            Search opens the {entity.label} page with these filters applied.
          </p>
        </div>
        {notice && (
          <span style={{
            fontSize: 12, fontWeight: 600,
            color: notice.kind === "ok" ? "#16A34A" : notice.kind === "err" ? "#B91C1C" : "#B45309",
          }}>
            {notice.msg}
          </span>
        )}
      </div>

      {/* Entity pills — which activity stream the filters below act on. */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {ACTIVITY_ENTITIES.map((e) => (
          <button
            key={e.key}
            onClick={() => pickEntity(e.key)}
            disabled={downloading}
            className={`qw-pill ${e.key === entityKey ? "qw-pill-indigo" : "qw-pill-slate"}`}
            style={{ cursor: "pointer", border: "none", fontSize: 12, padding: "5px 12px" }}
          >
            {e.label}
          </button>
        ))}
      </div>

      <div className="dyn-qw-filters dyn-qw-filters-q12">
        <div className="dyn-qw-filter-group qw-col-2">
          <label className="dyn-qw-filter-label">Customer</label>
          <input
            className="form-control"
            value={`${custID}${custName ? ` — ${custName}` : ""}`}
            title={`${custID}${custName ? ` — ${custName}` : ""}`}
            disabled
            readOnly
          />
        </div>
        {has("salesRepCode") && (
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Sales Rep</label>
            <SearchableSelect
              inputWidth="100%"
              config={salesRepConfig}
              value={filt.salesRepCode}
              onChange={(v) => set("salesRepCode", v)}
              placeholder={filt.territoryID ? `Reps in ${filt.territoryID}…` : "Code or name…"}
            />
          </div>
        )}
        {has("territoryID") && (
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Territory</label>
            <SearchableSelect
              inputWidth="100%"
              config={AUTOCOMPLETE_TERRITORY}
              value={filt.territoryID}
              onChange={(v) => set("territoryID", v)}
              placeholder="ID or description…"
            />
          </div>
        )}
        <div className="dyn-qw-filter-group qw-col-2">
          <label className="dyn-qw-filter-label">Status</label>
          <select className="form-select" value={filt.status} onChange={(e) => set("status", e.target.value)}>
            {entity.statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {has("poNum") && (
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">PO #</label>
            <input className="form-control" placeholder="Customer PO…" value={filt.poNum} onChange={(e) => set("poNum", e.target.value)} />
          </div>
        )}
        {has("orderNum") && (
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Order #</label>
            <input className="form-control" placeholder="Originating order…" value={filt.orderNum} onChange={(e) => set("orderNum", e.target.value)} />
          </div>
        )}
        {has("invoiced") && (
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Invoiced</label>
            <select className="form-select" value={filt.invoiced} onChange={(e) => set("invoiced", e.target.value)}>
              {ACTIVITY_INVOICED_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        )}
        {has("dateRange") && (
          <>
            <div className="dyn-qw-filter-group qw-col-2">
              <label className="dyn-qw-filter-label">Entry From</label>
              <input type="date" className="form-control" value={filt.dateFrom} onChange={(e) => set("dateFrom", e.target.value)} />
            </div>
            <div className="dyn-qw-filter-group qw-col-2">
              <label className="dyn-qw-filter-label">Entry To</label>
              <input type="date" className="form-control" value={filt.dateTo} onChange={(e) => set("dateTo", e.target.value)} />
            </div>
          </>
        )}
        <div className="dyn-qw-filter-group dyn-qw-actions-cell" style={{ gridColumn: "1 / -1" }}>
          <div className="d-flex gap-2" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
            <button type="button" className="btn btn-outline-secondary" disabled={downloading} onClick={onDownload}>
              {downloading
                ? <><Spinner animation="border" size="sm" style={{ marginRight: 6 }} /> Preparing…</>
                : <>⬇ Download</>}
            </button>
            <button type="button" className="btn btn-primary" style={{ fontWeight: 600 }} disabled={downloading} onClick={onSearch}>
              🔍 Search
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Audit tab → /api/jobs?relatedCustNum= ──────────────────────────────────────
function AuditTab({ custNum }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await apiFetch(`${API_URL}/api/jobs?relatedCustNum=${custNum}&top=10`);
        const d = await r.json().catch(() => ({}));
        if (!r.ok || d.success === false) throw new Error(humanError(r, d, "Audit history isn't available right now."));
        if (!cancelled) setJobs(d.jobs || d.Jobs || []);
      } catch (e) { if (!cancelled) setError(errorText(e, "Couldn't load audit history.")); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [custNum]);

  const pill = (status, comp) => {
    const s = String(status || "").toLowerCase();
    if (comp && String(comp).toLowerCase() === "rolledback") return "qw-pill-amber";
    if (s === "completed") return "qw-pill-emerald";
    if (s === "failed") return "qw-pill-amber";
    return "qw-pill-indigo";
  };

  return (
    <div className="qw-card qw-pad">
      <div className="qw-eyebrow">Change history</div>
      <p className="qw-sub" style={{ marginTop: 2, fontSize: 12 }}>Every saved change for this customer.</p>
      {loading && <div style={{ color: "#64748B", padding: "16px 0" }}><Spinner animation="border" size="sm" /> Loading…</div>}
      {error && <div className="qw-info qw-info-amber" style={{ marginTop: 10, background: "#FEF2F2", color: "#991B1B" }}>{error}</div>}
      {!loading && !error && jobs.length === 0 && <div style={{ color: "#94A3B8", fontSize: 13, marginTop: 8 }}>No orchestrated jobs recorded for this customer.</div>}
      <div className="qw-stack" style={{ marginTop: 10 }}>
        {jobs.map((j, i) => {
          const status = f(j, "status"); const comp = f(j, "compensationStatus");
          return (
            <div key={f(j, "jobId") || i} className="qw-card-flat qw-pad">
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, color: "#0F172A" }}>{f(j, "jobType")}</span>
                  <span className={"qw-pill " + pill(status, comp)}>{status || "—"}</span>
                  {comp && String(comp).toLowerCase() !== "none" && <span className="qw-pill qw-pill-amber">{comp}</span>}
                  <span className="qw-pill qw-pill-slate">{f(j, "stepCount", 0)} step(s)</span>
                </div>
                <div style={{ fontSize: 11, color: "#94A3B8" }}>{fmtDate(f(j, "createdAt"))}</div>
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                by {f(j, "createdBy") || "—"}
                {f(j, "failedReason") && <span style={{ color: "#B91C1C" }}> · {f(j, "failedReason")}</span>}
              </div>
              <div className="qw-mono" style={{ fontSize: 10.5, color: "#CBD5E1", marginTop: 2 }}>{f(j, "jobId")}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
